import {
  EMISSIONS_PER_TREE_IN_LBS_OF_CO2,
  EMISSIONS_PER_TREE_IN_KG_OF_CO2,
} from '../../../client_customizations/data/assumptions/ASSUMPTIONS';
import isBEV from '../../../functions/vehicle/isBEV';
import isPHEV from '../../../functions/vehicle/isPHEV';

const calcEmissions = {
  electricEmissions: function (consumptionRate, distance, userPrefs) {
    const electricEmissions = userPrefs?.get('electricEmissions');
    return (electricEmissions * consumptionRate * distance) / 1000 / 100;
  },

  gasolineEmissions: function (consumptionRate, distance, userPrefs) {
    const gasolineEmissions = userPrefs?.get('gasolineEmissions');
    return ((gasolineEmissions / 100) * distance) / consumptionRate;
  },

  emissions: function (
    vehicle,
    milesDrivenAnnually,
    electricMilesPortionForPhev,
    userPrefs
  ) {
    if (!vehicle) return 0;
    let emissions = 0;

    if (!isBEV(vehicle) && !isPHEV(vehicle)) {
      emissions = this.gasolineEmissions(
        vehicle.fossil_fuel_efficiency,
        milesDrivenAnnually,
        userPrefs
      );
    }

    if (isBEV(vehicle)) {
      emissions = this.electricEmissions(
        vehicle.electric_efficiency,
        milesDrivenAnnually,
        userPrefs
      );
    }

    if (isPHEV(vehicle)) {
      emissions =
        this.electricEmissions(
          vehicle.electric_efficiency,
          milesDrivenAnnually * (electricMilesPortionForPhev / 100),
          userPrefs
        ) +
        this.gasolineEmissions(
          vehicle.fossil_fuel_efficiency,
          milesDrivenAnnually * (1 - electricMilesPortionForPhev / 100),
          userPrefs
        );
    }
    return emissions;
  },

  emissionReduction: function (
    vehicle,
    milesDrivenAnnually,
    electricMilesPortionForPhev,
    userPrefs
  ) {
    return (
      this.emissions(
        vehicle.equivalent_gas_vehicle,
        milesDrivenAnnually,
        electricMilesPortionForPhev,
        userPrefs
      ) -
      this.emissions(
        vehicle,
        milesDrivenAnnually,
        electricMilesPortionForPhev,
        userPrefs
      )
    );
  },

  treesPlanted: function (
    vehicle,
    milesDrivenAnnually,
    electricMilesPortionForPhev,
    userPrefs
  ) {
    return (
      this.emissionReduction(
        vehicle,
        milesDrivenAnnually,
        electricMilesPortionForPhev,
        userPrefs
      ) /
      (process.env.REACT_APP_METRIC_SYSTEM
        ? EMISSIONS_PER_TREE_IN_KG_OF_CO2.value
        : EMISSIONS_PER_TREE_IN_LBS_OF_CO2.value)
    );
  },

  fuelUsed: function (
    vehicle,
    milesDrivenAnnually,
    electricMilesPortionForPhev
  ) {
    if (!vehicle) return 0;

    // All-electric --> no gasoline used
    if (isBEV(vehicle)) {
      return 0;
    }

    // Not electric --> all miles on gasoline
    if (!isBEV(vehicle) && !isPHEV(vehicle)) {
      return process.env.REACT_APP_METRIC_SYSTEM
        ? (milesDrivenAnnually * vehicle.fossil_fuel_efficiency) / 100
        : milesDrivenAnnually / vehicle.fossil_fuel_efficiency;
    }

    return process.env.REACT_APP_METRIC_SYSTEM
      ? ((1 - electricMilesPortionForPhev / 100) *
          milesDrivenAnnually *
          vehicle.fossil_fuel_efficiency) /
          100
      : ((1 - electricMilesPortionForPhev / 100) * milesDrivenAnnually) /
          vehicle.fossil_fuel_efficiency;
  },

  fuelSaved: function (
    vehicle,
    milesDrivenAnnually,
    electricMilesPortionForPhev
  ) {
    return (
      this.fuelUsed(
        vehicle.equivalent_gas_vehicle,
        milesDrivenAnnually,
        electricMilesPortionForPhev
      ) -
      this.fuelUsed(vehicle, milesDrivenAnnually, electricMilesPortionForPhev)
    );
  },
};

export default calcEmissions;
